import React, { useState, useEffect } from 'react';
import {
  Button,
  Container,
  Box,
  Modal,
  Tooltip,
  TextField,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';

// Static data for expense items
const staticExpenseItemData = [
  { expenseItemId: 1, expenseGroupName: 'Travel', expenseItemName: 'Flight Tickets', remark: 'Tickets for business trips' },
  { expenseItemId: 2, expenseGroupName: 'Travel', expenseItemName: 'Hotel Stay', remark: 'Accommodation for employees' },
  { expenseItemId: 3, expenseGroupName: 'Office Supplies', expenseItemName: 'Printer Paper', remark: 'Paper for office printers' },
  { expenseItemId: 4, expenseGroupName: 'Utilities', expenseItemName: 'Electricity Bill', remark: 'Monthly electricity bill' },
  { expenseItemId: 5, expenseGroupName: 'Salaries', expenseItemName: 'Monthly Salary', remark: 'Monthly employee salaries' },
  { expenseItemId: 6, expenseGroupName: 'Marketing', expenseItemName: 'Ad Campaign', remark: 'Online advertising campaign' },
];

// Static data for expense groups
const expenseGroups = [
  { id: 1, name: 'Travel' },
  { id: 2, name: 'Office Supplies' },
  { id: 3, name: 'Utilities' },
  { id: 4, name: 'Salaries' },
  { id: 5, name: 'Marketing' },
];

const EditExpenseItem = () => {
  const { expenseItemId } = useParams();
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [formData, setFormData] = useState({
    expenseGroup: '',
    expenseItem: '',
    remark: '',
  });

  const navigate = useNavigate();

  useEffect(() => {
    // Simulate fetching data for the expense item to edit
    const expenseItem = staticExpenseItemData.find(item => item.expenseItemId === parseInt(expenseItemId));
    if (expenseItem) {
      setFormData({
        expenseGroup: expenseItem.expenseGroupName,
        expenseItem: expenseItem.expenseItemName,
        remark: expenseItem.remark,
      });
    }
  }, [expenseItemId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleSave = (e) => {
    e.preventDefault();
    // Check if all required fields are filled
    if (!formData.expenseGroup || !formData.expenseItem || !formData.remark) {
      alert('Please fill all fields');
      return;
    }
    setShowSuccessModal(true);
  };

  const handleCancel = () => {
    navigate(-1);
  };

  return (
    <Container>
      <Box
        sx={{
          backgroundColor: 'rgba(2, 78, 101, 1)',
          padding: '12px 16px',
          boxShadow: 3,
          borderRadius: '4px 4px 0 0',
          margin: '0 auto',
          width: '100%',
        }}
      >
        <Typography variant="h4" sx={{ color: '#fff', fontWeight: 'bold' }}>
          Edit Expense Item
        </Typography>
      </Box>
      <Paper elevation={3} style={{ padding: 16, width: '100%', margin: 'auto', marginTop: 6, backgroundColor: '#f8fdfeff' }}>
        <form onSubmit={handleSave}>
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <FormControl fullWidth>
              <InputLabel id="expense-group-label">Expense Group</InputLabel>
              <Select
                labelId="expense-group-label"
                id="expenseGroup"
                name="expenseGroup"
                value={formData.expenseGroup}
                label="Expense Group"
                onChange={handleChange}
              >
                {expenseGroups.map((group) => (
                  <MenuItem key={group.id} value={group.name}>
                    {group.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField
              label="Expense Item"
              variant="outlined"
              fullWidth
              name="expenseItem"
              value={formData.expenseItem}
              onChange={handleChange}
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField
              label="Remark"
              variant="outlined"
              fullWidth
              name="remark"
              value={formData.remark}
              onChange={handleChange}
            />
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Tooltip
              title={
                (!formData.expenseGroup && 'Select Expense Group') ||
                (!formData.expenseItem && 'Enter Expense Item') ||
                (!formData.remark && 'Enter Remark')
              }
              arrow
            >
              <Button
                variant="contained"
                type="submit"
                sx={{ mr: 2, backgroundColor: '#127498ff', '&:hover': { backgroundColor: '#116280ff' } }}
                disabled={!formData.expenseGroup || !formData.expenseItem || !formData.remark}
              >
                SAVE
              </Button>
            </Tooltip>
            <Button variant="contained" color="error" onClick={handleCancel}>
              CANCEL
            </Button>
          </Box>
        </form>
      </Paper>
      <Modal
        open={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        aria-labelledby="success-modal-title"
        aria-describedby="success-modal-description"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            overflow: 'hidden',
          }}
        >
          <Box sx={{ backgroundColor: '#0B1F40', padding: '12px 16px' }}>
            <Typography id="success-modal-title" variant="h6" sx={{ color: '#fff' }}>
              Expense Item Updated ✅
            </Typography>
          </Box>
          <Box sx={{ padding: '20px' }}>
            <Typography id="success-modal-description" sx={{ mb: 2 }}>
              Expense Item Updated Successfully
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
              <Button
                onClick={() => {
                  setShowSuccessModal(false);
                  navigate(-1);
                }}
                variant="contained"
                color="primary"
              >
                OK
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>
    </Container>
  );
};

export default EditExpenseItem;
